import { useState, useMemo } from 'react';
import {
  BarChart3,
  TrendingUp,
  PieChart as PieChartIcon,
  Package,
  Calendar,
  DollarSign,
  ShoppingCart,
  AlertTriangle,
  Scale,
  Tag,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useDashboardStats, useSalesByPeriod } from '@/hooks/useStats';
import Button from '@/components/ui/Button';
import { formatStock, type Unit } from '@/utils/units';

// Colores para gráficos
const COLORS = ['#8b7355', '#a08060', '#b5906b', '#c9a076', '#deb082', '#d4a574', '#c9986a', '#be8b60'];
const PAYMENT_COLORS: Record<string, string> = {
  'cash': '#4ade80',
  'card': '#60a5fa',
  'transfer': '#a78bfa',
  'other': '#f472b6',
};

// Nombres amigables para métodos de pago
const PAYMENT_NAMES: Record<string, string> = {
  'cash': 'Efectivo',
  'card': 'Tarjeta',
  'transfer': 'Transferencia',
  'other': 'Otro',
};

type DatePreset = '7d' | '30d' | '90d' | '365d';

// Custom label renderer for pie charts
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderCustomLabel = (props: any) => {
  const { cx, cy, midAngle, outerRadius, name, percent } = props;
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 25;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const pct = ((percent || 0) * 100).toFixed(0);

  return (
    <text
      x={x}
      y={y}
      fill="#4a4a4a"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={11}
    >
      {`${name || ''} ${pct}%`}
    </text>
  );
};

export default function Reports() {
  const [datePreset, setDatePreset] = useState<DatePreset>('30d');
  // Agrupación de la tendencia (día/semana/mes) y métrica del top de productos
  const [periodGroup, setPeriodGroup] = useState<'day' | 'week' | 'month'>('week');
  const [topMetric, setTopMetric] = useState<'revenue' | 'units'>('units');

  const dateRange = useMemo(() => {
    const endDate = new Date();
    const startDate = new Date();

    switch (datePreset) {
      case '7d':
        startDate.setDate(startDate.getDate() - 7);
        break;
      case '30d':
        startDate.setDate(startDate.getDate() - 30);
        break;
      case '90d':
        startDate.setDate(startDate.getDate() - 90);
        break;
      case '365d':
        startDate.setDate(startDate.getDate() - 365);
        break;
    }

    return { startDate, endDate };
  }, [datePreset]);

  const { data: stats, isLoading, error } = useDashboardStats(dateRange);
  // Tendencia por período (día/semana/mes) — para ver el trend semanal
  const { data: periodData = [] } = useSalesByPeriod(dateRange, periodGroup);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-taupe-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
        Error al cargar las estadísticas. Por favor, intenta de nuevo.
      </div>
    );
  }

  if (!stats) return null;

  // Prepare data with labels for pie charts (con validación defensiva)
  const paymentMethodData = (stats.sales_by_payment_method || []).map(item => ({
    ...item,
    name: PAYMENT_NAMES[item.payment_method] || item.payment_method,
  }));

  const categoryData = (stats.sales_by_category || []).map(item => ({
    ...item,
    name: item.category_name,
  }));

  // Valores por defecto para evitar errores
  const salesSummary = stats.sales_summary || { total_revenue: 0, total_sales: 0, average_ticket: 0, total_items_sold: 0 };
  const topSellingProducts = stats.top_selling_products || [];
  const lowStockProducts = stats.low_stock_products || [];
  const fractionedProducts = stats.top_fractioned_products || [];
  const descuentos = stats.discounts_by_reason || [];
  const totalDescontado = descuentos.reduce((acc, d) => acc + Number(d.total_discounted || 0), 0);

  // Top de productos ordenado por la métrica elegida (unidades o ingresos)
  const topProductsData = [...topSellingProducts]
    .sort((a, b) => topMetric === 'units'
      ? (b.quantity_sold || 0) - (a.quantity_sold || 0)
      : (b.total_revenue || 0) - (a.total_revenue || 0))
    .slice(0, 8);

  // Etiqueta del eje X según agrupación de la tendencia
  const formatTrend = (period: string) => {
    const d = new Date(period);
    if (periodGroup === 'month') return d.toLocaleDateString('es-AR', { month: 'short', year: '2-digit' });
    if (periodGroup === 'week') return 'Sem ' + d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <BarChart3 className="h-7 w-7 text-taupe-600" />
            Reportes y Estadísticas
          </h1>
          <p className="text-gray-500 mt-1">Análisis del rendimiento del negocio</p>
        </div>

        {/* Date Range Selector */}
        <div className="flex gap-2">
          {[
            { value: '7d', label: '7 días' },
            { value: '30d', label: '30 días' },
            { value: '90d', label: '90 días' },
            { value: '365d', label: '1 año' },
          ].map((preset) => (
            <Button
              key={preset.value}
              variant={datePreset === preset.value ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setDatePreset(preset.value as DatePreset)}
            >
              {preset.label}
            </Button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-green-100 rounded-lg">
              <DollarSign className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Ingresos Totales</p>
              <p className="text-xl font-semibold text-gray-900">
                {formatCurrency(salesSummary.total_revenue)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-100 rounded-lg">
              <ShoppingCart className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Ventas</p>
              <p className="text-xl font-semibold text-gray-900">
                {salesSummary.total_sales}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-purple-100 rounded-lg">
              <TrendingUp className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Ticket Promedio</p>
              <p className="text-xl font-semibold text-gray-900">
                {formatCurrency(salesSummary.average_ticket)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-orange-100 rounded-lg">
              <Package className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Items Vendidos</p>
              <p className="text-xl font-semibold text-gray-900">
                {salesSummary.total_items_sold}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row 1: Sales by Period + Payment Methods */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales by Period - Line Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-taupe-600" />
              Tendencia de Ventas
            </h3>
            <div className="inline-flex p-0.5 bg-gray-100 rounded-lg">
              {([['day', 'Día'], ['week', 'Semana'], ['month', 'Mes']] as [typeof periodGroup, string][]).map(([g, label]) => (
                <button
                  key={g}
                  onClick={() => setPeriodGroup(g)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${periodGroup === g ? 'bg-white text-taupe-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >{label}</button>
              ))}
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={periodData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="period"
                  tickFormatter={formatTrend}
                  tick={{ fontSize: 12 }}
                  stroke="#9ca3af"
                />
                <YAxis
                  tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`}
                  tick={{ fontSize: 12 }}
                  stroke="#9ca3af"
                />
                <Tooltip
                  formatter={(value, name) => [
                    name === 'total_sales' ? `${value} ventas` : formatCurrency(Number(value)),
                    name === 'total_sales' ? 'Ventas' : 'Ingresos',
                  ]}
                  labelFormatter={formatTrend}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                />
                <Line
                  type="monotone"
                  dataKey="total_revenue"
                  stroke="#8b7355"
                  strokeWidth={2}
                  dot={{ fill: '#8b7355', strokeWidth: 2 }}
                  activeDot={{ r: 6, fill: '#8b7355' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Payment Methods - Pie Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <PieChartIcon className="h-5 w-5 text-taupe-600" />
            Métodos de Pago
          </h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={paymentMethodData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="total_revenue"
                  nameKey="name"
                  label={renderCustomLabel}
                  labelLine={{ stroke: '#9ca3af', strokeWidth: 1 }}
                >
                  {paymentMethodData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={PAYMENT_COLORS[entry.payment_method] || COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => formatCurrency(Number(value))}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Charts Row 2: Top Products + Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Selling Products - Horizontal Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <Package className="h-5 w-5 text-taupe-600" />
              Productos Más Vendidos
            </h3>
            <div className="inline-flex p-0.5 bg-gray-100 rounded-lg">
              {([['units', 'Unidades'], ['revenue', 'Ingresos']] as [typeof topMetric, string][]).map(([m, label]) => (
                <button
                  key={m}
                  onClick={() => setTopMetric(m)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${topMetric === m ? 'bg-white text-taupe-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >{label}</button>
              ))}
            </div>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={topProductsData}
                layout="vertical"
                margin={{ left: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis
                  type="number"
                  tickFormatter={(v) => topMetric === 'units' ? `${v}` : `$${(v/1000).toFixed(0)}k`}
                  tick={{ fontSize: 12 }}
                  stroke="#9ca3af"
                />
                <YAxis
                  type="category"
                  dataKey="product_name"
                  width={160}
                  tick={{ fontSize: 11 }}
                  stroke="#9ca3af"
                  tickFormatter={(v) => v.length > 25 ? v.substring(0, 25) + '...' : v}
                />
                <Tooltip
                  formatter={(value: any, _name: any, item: any) => [
                    topMetric === 'units'
                      ? formatStock(Number(value), (item?.payload?.display_unit as Unit) || 'unit')
                      : formatCurrency(Number(value)),
                    topMetric === 'units' ? 'Vendido' : 'Ingresos',
                  ]}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                />
                <Bar dataKey={topMetric === 'units' ? 'quantity_sold' : 'total_revenue'} fill="#8b7355" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Sales by Category - Pie Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <PieChartIcon className="h-5 w-5 text-taupe-600" />
            Ventas por Categoría
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="total_revenue"
                  nameKey="name"
                  label={renderCustomLabel}
                  labelLine={{ stroke: '#9ca3af', strokeWidth: 1 }}
                >
                  {categoryData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => formatCurrency(Number(value))}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Descuentos por motivo */}
      {descuentos.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <Tag className="h-5 w-5 text-taupe-600" />
              Descuentos por motivo
            </h3>
            <div className="text-right">
              <p className="text-xs text-gray-500">Total descontado</p>
              <p className="text-lg font-bold text-orange-600">{formatCurrency(totalDescontado)}</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-2 font-medium">Motivo</th>
                  <th className="pb-2 font-medium text-center">Ventas</th>
                  <th className="pb-2 font-medium text-right">Descontado</th>
                  <th className="pb-2 font-medium text-right">Vendido</th>
                  <th className="pb-2 font-medium text-right">% del total</th>
                </tr>
              </thead>
              <tbody>
                {descuentos.map((d) => (
                  <tr key={d.reason_id} className="border-b border-gray-50">
                    <td className="py-2 font-medium text-gray-900">{d.reason_name}</td>
                    <td className="py-2 text-center text-gray-600">{d.sales_count}</td>
                    <td className="py-2 text-right font-medium text-orange-600">
                      -{formatCurrency(d.total_discounted)}
                    </td>
                    <td className="py-2 text-right text-gray-600">{formatCurrency(d.total_sold)}</td>
                    <td className="py-2 text-right text-gray-500">
                      {totalDescontado > 0
                        ? `${Math.round((Number(d.total_discounted) / totalDescontado) * 100)}%`
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 mt-3">
            No incluye el canje de puntos VIP, que se descuenta aparte y no es un
            descuento que alguien haya decidido dar.
          </p>
        </div>
      )}

      {/* Fraccionados mas vendidos */}
      {fractionedProducts.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Scale className="h-5 w-5 text-taupe-600" />
            Fraccionados Más Vendidos
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-2 font-medium">Producto</th>
                  <th className="pb-2 font-medium">Se arma de</th>
                  <th className="pb-2 font-medium text-center">Unidades</th>
                  <th className="pb-2 font-medium text-center">Equivale a</th>
                  <th className="pb-2 font-medium text-right">Facturado</th>
                </tr>
              </thead>
              <tbody>
                {fractionedProducts.map((p) => (
                  <tr key={p.product_id} className="border-b border-gray-50">
                    <td className="py-2 font-medium text-gray-900">{p.product_name}</td>
                    <td className="py-2 text-gray-600">
                      {p.source_name || '-'}
                      {p.fraction_grams > 0 && (
                        <span className="text-gray-400 text-xs ml-1">
                          ({formatStock(p.fraction_grams, p.fraction_grams >= 1000 ? 'kilogram' : 'gram')} c/u)
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-center font-medium text-gray-900">
                      {formatStock(p.units_sold, 'unit')}
                    </td>
                    <td className="py-2 text-center text-gray-600">
                      {p.grams_sold > 0 ? formatStock(p.grams_sold, p.grams_sold >= 1000 ? 'kilogram' : 'gram') : '-'}
                    </td>
                    <td className="py-2 text-right font-medium text-gray-900">
                      {formatCurrency(p.total_revenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Productos armados desde un granel padre. La columna "Equivale a" es lo que se
            descontó del stock del granel en el período.
          </p>
        </div>
      )}

      {/* Low Stock Alert */}
      {lowStockProducts.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-amber-200 p-5">
          <h3 className="text-lg font-medium text-amber-800 mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Productos con Stock Bajo ({lowStockProducts.length})
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-2 font-medium">Producto</th>
                  <th className="pb-2 font-medium">Categoría</th>
                  <th className="pb-2 font-medium text-center">Stock Actual</th>
                  <th className="pb-2 font-medium text-center">Stock Mínimo</th>
                  <th className="pb-2 font-medium text-center">Estado</th>
                </tr>
              </thead>
              <tbody>
                {lowStockProducts.slice(0, 10).map((product) => (
                  <tr key={product.id} className="border-b border-gray-50">
                    <td className="py-2 font-medium text-gray-900">{product.name}</td>
                    <td className="py-2 text-gray-600">{product.category_name || '-'}</td>
                    <td className="py-2 text-center">
                      <span className={`font-medium ${product.stock === 0 ? 'text-red-600' : 'text-amber-600'}`}>
                        {formatStock(Number(product.stock), product.display_unit || 'unit')}
                      </span>
                    </td>
                    <td className="py-2 text-center text-gray-600">
                      {formatStock(Number(product.minimum_stock), product.display_unit || 'unit')}
                    </td>
                    <td className="py-2 text-center">
                      {product.stock === 0 ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Sin stock
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          Bajo
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
